import axios from 'axios';
import { prepareDateTimeForHttpRequest } from 'helpers/dateHelper';
import { SlipStatus } from 'types/enums';
import { LiveEventOdd } from 'types/LiveEvent';
import { Slip } from 'types/Slip';
import { SlipEntry } from 'types/SlipEntry';
import { UserInfo } from 'types/UserInfo';
import { mapHttpResponse } from 'utils/slip.utils';
import { MultiBonusRules } from 'utils/multiBonus';
import { requestBalanceRefresh } from './balanceService';

export type LiveQuoteLeg = {
  event_id: string | number;
  odd_custom_id: string | number;
  bet_name: string;
  odds_client: number | null;
  odds_server: number | null;
  changed: boolean;
  available: boolean;
  /** The provider did not answer: the price is unknown, the pick stays. */
  unconfirmed?: boolean;
  /** A prematch pick whose match has kicked off. */
  started?: boolean;
};

function slipLegKey(fi: string | number, customId: string | number): string {
  return `${fi}|${customId}`;
}

/**
 * Same rows as create_ticket; every row carries odds_client for verify/compare.
 * Optional `oddsClientOverrides` (e.g. last server prices) for a re-check before submit.
 */
export function buildTicketEventsPayload(
  entries: SlipEntry[],
  oddsClientOverrides?: Map<string, number>
): Record<string, string | number | boolean | null | undefined>[] {
  const ticket_events: Record<
    string,
    string | number | boolean | null | undefined
  >[] = [];
  entries.forEach((x) => {
    x.bets.forEach((y) => {
      if (y.disabled) return;
      const row: Record<string, string | number | boolean | null | undefined> =
        {
          event_id: x.match.fi,
          bet_name: y.bet.realName ?? y.bet.name,
          bet_name_second: x.isLive ? (y.odd as LiveEventOdd).name3 : '',
          odd_name: y.odd.name,
          odd_custom_id: y.odd.custom_id,
          is_live: `${x.isLive}`,
        };
      // The price the player saw, for the server to compare with the
      // provider's current one (live and prematch alike).
      const key = slipLegKey(x.match.fi, y.odd.custom_id);
      row.odds_client = oddsClientOverrides?.get(key) ?? y.odd.odds;
      if (x.isLive) {
        const ku = (x.match as { kickoffUnix?: number }).kickoffUnix;
        if (typeof ku === 'number' && Number.isFinite(ku) && ku > 0) {
          row.event_start_time = Math.trunc(ku);
        }
      }
      ticket_events.push(row);
    });
  });
  return ticket_events;
}

export async function verifyLiveQuotes(
  ticket_events: Record<string, string | number | boolean | null | undefined>[]
): Promise<{ legs: LiveQuoteLeg[]; retryLeft: number } | null> {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.post<{
      legs: LiveQuoteLeg[];
      retry_left?: number;
    }>(`${baseUrl}/verify_live_quotes`, { ticket_events });
    return { legs: data?.legs ?? [], retryLeft: data?.retry_left ?? 0 };
  } catch (e) {
    console.error(e);
    return null;
  }
}

/** The player said no to trying the check again; recorded for staff. */
export async function declineSlipCheck(): Promise<void> {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    await axios.post(`${baseUrl}/slip_decline`);
  } catch (e) {
    console.error(e);
  }
}

/** Live prices for a slip still being put together; for showing only. */
export async function fetchSlipQuotes(
  ticket_events: Record<string, string | number | boolean | null | undefined>[]
): Promise<LiveQuoteLeg[]> {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.post<{ legs: LiveQuoteLeg[] }>(
      `${baseUrl}/slip_quotes`,
      { ticket_events }
    );
    return data?.legs ?? [];
  } catch (e) {
    console.error(e);
    return [];
  }
}

export type CreateSlipResult =
  | { ok: true }
  | {
      ok: false;
      error: string;
      insufficientFunds: boolean;
      /** Refused by the player's own limit or self-exclusion (code from the API). */
      code?: 'limit_reached' | 'account_locked';
      limit?: { kind: string; period: string; remaining: number };
    };

export const createSlip = async (
  entries: SlipEntry[],
  stake: number,
  loggedUser: UserInfo,
  /** System sizes, [7] for "7 of 9"; left out for an ordinary ticket. */
  system?: number[]
): Promise<CreateSlipResult> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  const requestBody: any = {
    stake,
    player_id: loggedUser.id,
    ticket_events: buildTicketEventsPayload(entries),
  };
  if (system && system.length > 0) requestBody.system = system;
  try {
    await axios.post(`${baseUrl}/create_ticket`, requestBody);
    // The stake is gone from the balance; don't wait for the websocket.
    requestBalanceRefresh();
    return { ok: true };
  } catch (e: unknown) {
    const err = e as { response?: { data?: Record<string, any> } };
    const data = err?.response?.data ?? {};
    const message = String(data.error ?? '').trim() || 'Ticket creation failed';
    console.error(e);
    const insufficientFunds = /not enough balance|insufficient/i.test(message);
    if (data.code === 'limit_reached' || data.code === 'account_locked') {
      return {
        ok: false,
        error: message,
        insufficientFunds: false,
        code: data.code,
        limit:
          data.code === 'limit_reached'
            ? {
                kind: String(data.kind),
                period: String(data.period),
                remaining: Number(data.remaining) || 0,
              }
            : undefined,
      };
    }
    return { ok: false, error: message, insufficientFunds };
  }
};

export const getSlips = async (
  user: UserInfo,
  ticketStatus: SlipStatus,
  startDate?: Date,
  endDate?: Date,
  onlyLiveBets: boolean = false
): Promise<Slip[] | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  const startDateStr = prepareDateTimeForHttpRequest(startDate);

  let endDateStr = '';
  if (endDate) {
    endDateStr = prepareDateTimeForHttpRequest(endDate);
  }

  try {
    const results = await axios.get(
      `${baseUrl}/get_tickets?player_id=${user.id}&status=${ticketStatus}&from_time=${startDateStr}&to_time=${endDateStr}${onlyLiveBets ? '&is_live=True' : ''}`
    );

    return mapHttpResponse(results.data);
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** A cash out offer for one open ticket (see the API's bets/cashout.go). */
export interface CashoutOffer {
  ticket_id: number;
  available: boolean;
  amount?: number;
  reason?: string;
  /** How long accepting takes (the server's check), for the countdown. */
  wait_ms?: number;
}

export const getCashoutOffers = async (): Promise<{
  enabled: boolean;
  offers: CashoutOffer[];
} | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.get(`${baseUrl}/cashout_offers`);
    return {
      enabled: !!data?.enabled,
      offers: Array.isArray(data?.offers) ? data.offers : [],
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

export type CashoutResult =
  | { ok: true; amount: number }
  | { ok: false; offer?: CashoutOffer; error: string };

/** Accepts an offer. The server waits a few seconds and checks the price
 * again; a lower offer comes back as `offer` for the player to look at. */
export const cashoutTicket = async (
  ticketId: number,
  amount: number
): Promise<CashoutResult> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.post(`${baseUrl}/cashout`, {
      ticket_id: ticketId,
      amount,
    });
    requestBalanceRefresh();
    return { ok: true, amount: Number(data?.amount ?? amount) };
  } catch (e: unknown) {
    const err = e as { response?: { data?: Record<string, any> } };
    const data = err?.response?.data ?? {};
    return {
      ok: false,
      offer: data.offer,
      error: String(data.error ?? 'cashout_failed'),
    };
  }
};

/** Cancels a prematch ticket within its storno time; the stake goes back. */
export const cancelTicket = async (
  ticketId: number
): Promise<{ ok: boolean; error?: string }> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    await axios.post(`${baseUrl}/cancel_ticket`, { ticket_id: ticketId });
    requestBalanceRefresh();
    return { ok: true };
  } catch (e: unknown) {
    const err = e as { response?: { data?: Record<string, any> } };
    return { ok: false, error: String(err?.response?.data?.error ?? '') };
  }
};

/** The bonus on the number of matches; off when the request fails. */
export const getMultiBonus = async (): Promise<MultiBonusRules> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.get(`${baseUrl}/multi_bonus`);
    return data?.enabled ? (data as MultiBonusRules) : { enabled: false };
  } catch (e) {
    console.error(e);
    return { enabled: false };
  }
};
